/* Trading IA — SIMULATION crypto (paper trading). Portefeuille virtuel 100 000 USD.
 * L'IA analyse le marché (CoinGecko) et exécute des ordres SIMULÉS si activé.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Bot, Loader2, Play, TrendingUp, TrendingDown, Wallet, LineChart as LineChartIcon,
  AlertTriangle, Power, RefreshCw,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const fmtUSD = (v) => (v == null ? "—" : v.toLocaleString("fr-CH", { maximumFractionDigits: 0 }) + " $");

export default function TradingIA() {
  const [pf, setPf] = useState(null);
  const [trades, setTrades] = useState([]);
  const [equity, setEquity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [p, t, e] = await Promise.all([
        api.get("/workspace/trading/portfolio"),
        api.get("/workspace/trading/trades"),
        api.get("/workspace/trading/equity"),
      ]);
      setPf(p.data);
      setTrades(t.data.trades || []);
      setEquity(e.data.points || []);
      if (p.data.run_status !== "generating" && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadAll]);

  useEffect(() => {
    if (pf?.run_status === "generating" && !pollRef.current) {
      pollRef.current = setInterval(loadAll, 4000);
    }
  }, [pf?.run_status, loadAll]);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/trading/toggle", { enabled: !pf.enabled });
      setPf((prev) => ({ ...prev, enabled: r.data.enabled }));
      notify.success(r.data.enabled
        ? "Trading IA activé — l'IA tradera automatiquement toutes les 4 h (simulation)."
        : "Trading IA désactivé.");
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/workspace/trading/run-now");
      setPf((prev) => ({ ...prev, run_status: "generating" }));
      if (!pollRef.current) pollRef.current = setInterval(loadAll, 4000);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  if (loading) {
    return <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;
  }

  const generating = pf?.run_status === "generating";
  const perf = pf?.perf_pct || 0;

  return (
    <div className="space-y-4" data-testid="trading-ia-root">
      <div className="noam-card p-3 flex items-center gap-2 text-xs border-[#EF9F27]/30 text-zinc-300">
        <AlertTriangle className="w-4 h-4 text-[#EF9F27] shrink-0" />
        <span>
          <strong>SIMULATION</strong> — portefeuille virtuel de 100 000 $ : aucun argent réel, aucun courtier.
          Les analyses et ordres sont simulés aux prix de marché CoinGecko; aucun ordre n’est transmis à un courtier.
        </span>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border transition-colors ${
              pf?.enabled
                ? "bg-[#1D9E75]/15 text-[#1D9E75] border-[#1D9E75]/40"
                : "bg-white/5 text-zinc-400 border-white/10 hover:text-white"
            }`}
            data-testid="trading-toggle-btn"
          >
            <Power className="w-3.5 h-3.5" />
            {pf?.enabled ? "Trading IA actif (auto 4 h)" : "Trading IA désactivé"}
          </button>
          <button
            type="button"
            onClick={runNow}
            disabled={busy || generating}
            className="btn-primary text-xs"
            data-testid="trading-run-now-btn"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {generating ? "Cycle IA en cours…" : "Lancer un cycle IA maintenant"}
          </button>
        </div>
        <button type="button" onClick={loadAll} className="btn-ghost text-xs" data-testid="trading-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </button>
      </div>

      {pf?.run_status === "error" && (
        <div className="text-xs text-[#E24B4A]">Dernier cycle en erreur : {pf.run_error?.toLowerCase().includes("budget")
            ? "Budget du fournisseur IA atteint. Aucun ordre simulé n’a été créé pendant ce cycle."
            : "Le cycle IA a échoué. Les détails techniques sont masqués."}</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Équité totale" value={fmtUSD(pf?.equity)} icon={Wallet} />
        <Stat label="Cash disponible" value={fmtUSD(pf?.cash)} icon={Wallet} />
        <Stat label="Positions" value={fmtUSD(pf?.holdings_value)} icon={LineChartIcon} />
        <Stat
          label="Performance"
          value={`${perf >= 0 ? "+" : ""}${perf.toFixed(2)} %`}
          icon={perf >= 0 ? TrendingUp : TrendingDown}
          tone={perf >= 0 ? "up" : "down"}
        />
      </div>

      {pf?.last_market_view && (
        <div className="noam-card p-3 text-xs text-zinc-300 flex items-start gap-2" data-testid="trading-market-view">
          <Bot className="w-4 h-4 text-[#378ADD] shrink-0 mt-0.5" />
          <span><span className="text-zinc-500">Lecture IA du marché — </span>{pf.last_market_view}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="noam-card p-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Courbe d'équité (simulation)</div>
          {equity.length < 2 ? (
            <div className="text-xs text-zinc-500 py-8 text-center">Lancez au moins 2 cycles IA pour tracer la courbe.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={equity.map((p) => ({ ...p, label: new Date(p.at).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit" }) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#71717a" }} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10, fill: "#71717a" }} width={70} />
                <Tooltip contentStyle={{ background: "#14161c", border: "1px solid #ffffff20", fontSize: 12 }} />
                <Line type="monotone" dataKey="equity" stroke="#378ADD" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="noam-card p-4" data-testid="trading-positions">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Positions ouvertes</div>
          {(pf?.positions || []).length === 0 ? (
            <div className="text-xs text-zinc-500 py-8 text-center">Aucune position — 100 % cash.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase text-zinc-500 border-b border-white/10">
                  <th className="py-1.5">Actif</th><th>Qté</th><th>Coût moyen</th><th>Prix</th><th>Valeur</th><th>P&L</th>
                </tr>
              </thead>
              <tbody>
                {pf.positions.map((p) => (
                  <tr key={p.coin_id} className="border-b border-white/5">
                    <td className="py-1.5 font-medium">{p.symbol}</td>
                    <td>{p.qty.toFixed(5)}</td>
                    <td>{p.avg_cost?.toFixed(2)}</td>
                    <td>{p.price?.toFixed(2)}</td>
                    <td>{fmtUSD(p.value)}</td>
                    <td className={p.pnl_pct >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>
                      {p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="noam-card p-4" data-testid="trading-journal">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Journal des ordres IA (simulés)</div>
        {trades.length === 0 ? (
          <div className="text-xs text-zinc-500 py-6 text-center">Aucun ordre encore — lancez un cycle IA.</div>
        ) : (
          <div className="space-y-2">
            {trades.map((t) => (
              <div key={t.id} className="border border-white/10 rounded-md p-2.5 text-xs">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${
                      t.side === "buy" ? "bg-[#1D9E75]/15 text-[#1D9E75]" : "bg-[#E24B4A]/15 text-[#E24B4A]"
                    }`}>
                      {t.side === "buy" ? "ACHAT" : "VENTE"}
                    </span>
                    <span className="font-medium">{t.coin_id}</span>
                    <span className="text-zinc-500">{t.qty?.toFixed(5)} @ {t.price?.toFixed(2)} $ = {fmtUSD(t.value)}</span>
                    {t.pnl_pct != null && (
                      <span className={t.pnl_pct >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>
                        ({t.pnl_pct >= 0 ? "+" : ""}{t.pnl_pct.toFixed(1)}%)
                      </span>
                    )}
                  </div>
                  <span className="text-zinc-600">{new Date(t.at).toLocaleString("fr-CH")} · {t.trigger === "auto" ? "auto" : "manuel"}</span>
                </div>
                {t.reason && <div className="text-zinc-400 mt-1"><Bot className="w-3 h-3 inline mr-1 text-[#378ADD]" />{t.reason}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  return (
    <div className="noam-card p-3">
      <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase tracking-wider">
        <Icon className={`w-3 h-3 ${tone === "up" ? "text-[#1D9E75]" : tone === "down" ? "text-[#E24B4A]" : "text-[#378ADD]"}`} /> {label}
      </div>
      <div className={`text-xl font-semibold mt-1 ${tone === "up" ? "text-[#1D9E75]" : tone === "down" ? "text-[#E24B4A]" : ""}`}>{value}</div>
    </div>
  );
}
